import type { FC } from "react";
import { lazy, Suspense, useEffect, useState } from "react";

import { LegalBodySkeleton } from "@/features/legal/LegalBodySkeleton.tsx";
import type { LegalDocumentKey } from "@/features/legal/legalTexts.ts";
import { ContentOverlay } from "@/components/ContentOverlay.tsx";
import { i18n } from "@/localization";
import type { TranslationKey } from "@/localization/resources/keys.ts";

export type { LegalDocumentKey };

/*
 * The loader and the lazy() factory share one specifier on purpose: Rollup resolves them to a
 * single chunk and the module registry dedupes the fetch, so preloading and rendering cost one
 * request between them.
 */
const loadLegalBody = () => import("@/features/legal/LegalBody.tsx");
const LegalBody = lazy(loadLegalBody);

const TITLE_KEYS: Record<LegalDocumentKey, TranslationKey> = {
	terms: "legal.termsTitle",
	privacy: "legal.privacyTitle"
};

/** Warms the chunk on hover and focus so the click renders the text without a fallback. */
export function preloadLegalDocument(): void {
	void loadLegalBody();
}

interface LegalDocumentOverlayProps {
	/** The document to show, or null when the overlay is closed. */
	doc: LegalDocumentKey | null;
	onClose: () => void;
}

/**
 * Shows one of the binding legal documents in the shared printable overlay.
 *
 * The Suspense boundary sits around the body rather than the whole overlay so the panel never
 * unmounts: the header, print button and backdrop paint in the click's own frame and only the
 * text swaps in behind them.
 */
export const LegalDocumentOverlay: FC<LegalDocumentOverlayProps> = ({ doc, onClose }) => {
	const [ready, setReady] = useState(false);

	useEffect(() => {
		if (!doc) {
			setReady(false);

			return;
		}

		let cancelled = false;

		setReady(false);
		void loadLegalBody().then(() => {
			if (!cancelled) {
				setReady(true);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [doc]);

	if (!doc) {
		return null;
	}

	return (
		<ContentOverlay
			open
			onClose={onClose}
			title={i18n(TITLE_KEYS[doc])}
			printLabel={i18n("legal.print")}
			closeLabel={i18n("legal.close")}
			// Printing the skeleton would hand the user a blank sheet.
			printDisabled={!ready}
		>
			<Suspense fallback={<LegalBodySkeleton />}>
				<LegalBody doc={doc} />
			</Suspense>
		</ContentOverlay>
	);
};
